import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui'
import { Plus, Trash2, Save, AlertCircle, Check } from 'lucide-react'

interface PortfolioEntry {
  fund_id: number
  fund_name: string
  value: number
  weight: number
  data_source: string
}

interface DemoPortfolio {
  id: number
  name: string
  description: string
  created_by: string
  created_at: string
  updated_at: string
  total_value: number
  fund_count: number
  portfolio_data?: PortfolioEntry[]
}

export default function DemoPortfolios() {
  const queryClient = useQueryClient()
  const [activePortfolioId, setActivePortfolioId] = useState<number | null>(null)
  const [entries, setEntries] = useState<PortfolioEntry[]>([])
  const [portfolioName, setPortfolioName] = useState('')
  const [portfolioDesc, setPortfolioDesc] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load portfolio list
  const { data: portfolios, isLoading: listLoading } = useQuery({
    queryKey: ['demo-portfolios'],
    queryFn: () => api.get('/demo-portfolios').then((r) => r.data),
  })

  // Search funds
  const { data: searchResults } = useQuery({
    queryKey: ['fund-search', searchTerm],
    queryFn: () => api.get(`/demo-portfolios/search/funds?q=${encodeURIComponent(searchTerm)}&limit=30`).then((r) => r.data),
    enabled: searchTerm.length >= 2,
  })

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (payload: any) => {
      if (activePortfolioId) {
        return api.put(`/demo-portfolios/${activePortfolioId}`, payload).then((r) => r.data)
      }
      return api.post('/demo-portfolios', payload).then((r) => r.data)
    },
    onSuccess: (data) => {
      setMessage({ type: 'success', text: 'Portfolio saved successfully' })
      setActivePortfolioId(data.id)
      queryClient.invalidateQueries({ queryKey: ['demo-portfolios'] })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: (err: any) => {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/demo-portfolios/${id}`),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Portfolio deleted' })
      if (activePortfolioId) {
        setActivePortfolioId(null)
        setEntries([])
        setPortfolioName('')
        setPortfolioDesc('')
      }
      queryClient.invalidateQueries({ queryKey: ['demo-portfolios'] })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  const totalValue = useMemo(() => entries.reduce((s, e) => s + e.value, 0), [entries])

  const entriesWithWeights = useMemo(() => {
    return entries.map((e) => ({
      ...e,
      weight: totalValue > 0 ? (e.value / totalValue) * 100 : 0,
    }))
  }, [entries, totalValue])

  const loadPortfolio = async (id: number) => {
    try {
      const res = await api.get(`/demo-portfolios/${id}`)
      const p = res.data
      setActivePortfolioId(p.id)
      setPortfolioName(p.name)
      setPortfolioDesc(p.description || '')
      setEntries(p.portfolio_data || [])
    } catch {
      setMessage({ type: 'error', text: 'Failed to load portfolio' })
    }
  }

  const addFund = (fund: any) => {
    if (entries.some((e) => e.fund_id === fund.fund_id && e.data_source === fund.data_source)) return
    setEntries((prev) => [
      ...prev,
      {
        fund_id: fund.fund_id,
        fund_name: fund.fund_name,
        value: 10000000,
        weight: 0,
        data_source: fund.data_source,
      },
    ])
    setSearchTerm('')
  }

  const removeFund = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx))
  }

  const updateValue = (idx: number, value: number) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, value } : e)))
  }

  const handleSave = () => {
    if (!portfolioName.trim()) {
      setMessage({ type: 'error', text: 'Portfolio name is required' })
      return
    }
    saveMutation.mutate({
      name: portfolioName,
      description: portfolioDesc,
      portfolio_data: entriesWithWeights,
    })
  }

  const handleNew = () => {
    setActivePortfolioId(null)
    setEntries([])
    setPortfolioName('')
    setPortfolioDesc('')
  }

  const filteredSearch = useMemo(() => {
    if (!searchResults) return []
    const existing = new Set(entries.map((e) => `${e.fund_id}_${e.data_source}`))
    return searchResults.filter((f: any) => !existing.has(`${f.fund_id}_${f.data_source}`))
  }, [searchResults, entries])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Demo Portfolios</h1>
          <p className="text-muted-foreground">Build and save portfolio allocations</p>
        </div>
        <button
          onClick={handleNew}
          className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Portfolio
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Saved portfolios sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h2 className="text-sm font-semibold">Saved Portfolios</h2>
            </div>
            {listLoading ? (
              <LoadingSpinner message="Loading..." />
            ) : !portfolios?.length ? (
              <div className="p-4 text-sm text-muted-foreground">No saved portfolios</div>
            ) : (
              <div className="divide-y divide-border">
                {portfolios.map((p: DemoPortfolio) => (
                  <div
                    key={p.id}
                    className={`p-3 cursor-pointer hover:bg-accent/50 ${activePortfolioId === p.id ? 'bg-accent' : ''}`}
                    onClick={() => loadPortfolio(p.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('Delete this portfolio?')) deleteMutation.mutate(p.id)
                        }}
                        className="p-1 text-muted-foreground hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {p.fund_count} funds &middot; {formatCurrency(p.total_value)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Portfolio editor */}
        <div className="lg:col-span-3 space-y-4">
          {/* Name + description */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Portfolio Name</label>
                <input
                  type="text"
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                  placeholder="My Portfolio"
                  value={portfolioName}
                  onChange={(e) => setPortfolioName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Description</label>
                <input
                  type="text"
                  className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                  placeholder="Optional description..."
                  value={portfolioDesc}
                  onChange={(e) => setPortfolioDesc(e.target.value)}
                />
              </div>
            </div>

            {/* Fund search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search funds to add..."
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm.length >= 2 && filteredSearch.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {filteredSearch.map((f: any) => (
                    <button
                      key={`${f.fund_id}_${f.data_source}`}
                      onClick={() => addFund(f)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between"
                    >
                      <span className="flex items-center gap-2">
                        <Plus className="h-3 w-3 text-muted-foreground" />
                        {f.fund_name}
                      </span>
                      <span className="text-xs text-muted-foreground">{f.data_source}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Portfolio entries */}
          {entries.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
              Search and add funds above, or load a saved portfolio from the sidebar
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Fund</th>
                    <th>Source</th>
                    <th className="text-right">Value ($)</th>
                    <th className="text-right">Weight</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {entriesWithWeights.map((entry, idx) => (
                    <tr key={`${entry.fund_id}_${entry.data_source}`}>
                      <td className="font-medium">{entry.fund_name}</td>
                      <td className="text-xs text-muted-foreground">{entry.data_source}</td>
                      <td className="text-right">
                        <input
                          type="number"
                          step="100000"
                          min="0"
                          className="w-32 h-8 px-2 border border-input rounded-md bg-background text-sm text-right"
                          value={entry.value || ''}
                          onChange={(e) => updateValue(idx, parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="text-right font-mono text-muted-foreground">
                        {formatNumber(entry.weight, 1)}%
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => removeFund(idx)}
                          className="p-1.5 text-muted-foreground hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border font-medium">
                    <td colSpan={2}>Total</td>
                    <td className="text-right font-mono">{formatCurrency(totalValue)}</td>
                    <td className="text-right font-mono">100.0%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Save button */}
          {entries.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span>{entries.length} funds</span>
                <span>{formatCurrency(totalValue)} total</span>
              </div>
              <div className="flex gap-2">
                <a
                  href="/dashboard/portfolio/simulation"
                  className="h-9 px-4 border border-input rounded-md text-sm font-medium hover:bg-accent flex items-center gap-2"
                >
                  Simulate
                </a>
                <button
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                  className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saveMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
